import * as React from 'react';
import * as _ from 'lodash';
import * as styles from './pagination.module.scss';
import MaterialIcon from '@material/react-material-icon';
import PageCount from './page-count';
import PagesDisplay from './pages-display';
import OutlinedInput from '../../common/outlined-input';
import PaginatedQueryFunction from './paginated-query-function';
import { ContextValue } from './paginated-context';

/**
 * Pagination
 * @function Pagination - Component to render pagination of results.
 * @param {PaginatedQueryFunction} fetchResults - Function to query for the pages of results.
 * @param {React.Context<ContextValue<any>>} paginatedContext - Context for the results and pages.
 * @param {string} searchTerm - Search term for the query.
 * @param {Object} fetchExtraProps - Optional object with extra properties to include in the fetchResults function call.
 */
const Pagination = ({
  fetchResults,
  paginatedContext,
  searchTerm,
  fetchExtraProps
}: {
  fetchResults: PaginatedQueryFunction;
  paginatedContext: React.Context<ContextValue<any>>;
  searchTerm: string;
  fetchExtraProps?: Object;
}) => {
  const { pageSize, currentPage, totalResults, orderColumn, orderDirection } = React.useContext(paginatedContext);
  const [inputPage, setInputPage] = React.useState<number | null>(null);

  const totalPages = Math.ceil(totalResults / pageSize);
  const allPages = _.range(1, totalPages + 1);

  /**
   * handlePageChange
   * @function handlePageChange - Fetches the results after the user selects the number page
   * @param newPage Selects which page will be requested
   */
  const handlePageChange = (newPage: number) => {
    if (newPage > totalPages) {
      newPage = totalPages;
    } else if (newPage < 1) {
      newPage = 1;
    }
    fetchResults({
      pageSize,
      currentPage: newPage,
      searchTerm,
      orderColumn,
      orderDirection,
      ...fetchExtraProps
    });
  };

  // Clears out the number in input if the selectedPage is changed outside the input
  React.useEffect(() => {
    if (currentPage !== inputPage) {
      setInputPage(null);
    }
  }, [currentPage]);

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.pageSelection}>
        <span>Show: </span>
        <PageCount
          count={15}
          currentPageSize={pageSize}
          searchTerm={searchTerm}
          orderColumn={orderColumn}
          orderDirection={orderDirection}
          fetchResults={fetchResults}
          fetchExtraProps={fetchExtraProps}
        />
        <PageCount
          count={30}
          currentPageSize={pageSize}
          searchTerm={searchTerm}
          orderColumn={orderColumn}
          orderDirection={orderDirection}
          fetchResults={fetchResults}
          fetchExtraProps={fetchExtraProps}
        />
        <PageCount
          count={60}
          currentPageSize={pageSize}
          searchTerm={searchTerm}
          orderColumn={orderColumn}
          orderDirection={orderDirection}
          fetchResults={fetchResults}
          fetchExtraProps={fetchExtraProps}
        />
      </div>
      <div className={styles.pageNumbers}>
        {currentPage !== 1 ? (
          <MaterialIcon
            icon="keyboard_arrow_left"
            className={styles.paginationControl}
            onClick={() => handlePageChange(currentPage - 1)}
          />
        ) : (
          <div />
        )}
        <PagesDisplay
          totalPages={totalPages}
          allPages={allPages}
          selectedPage={currentPage}
          activeStyles={styles.activePage}
          handlePageChange={(page: number) => handlePageChange(page)}
        />
        {currentPage !== totalPages && totalPages > 0 ? (
          <MaterialIcon
            icon="keyboard_arrow_right"
            className={styles.paginationControl}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        ) : (
          <div />
        )}
      </div>
      <div className={styles.pageSelection}>
        <span>Go To Page:</span>
        <form
          onSubmit={event => {
            event.preventDefault();
            handlePageChange(inputPage);
          }}
        >
          <OutlinedInput
            id="outlined-page-input"
            margin="dense"
            variant="outlined"
            type="number"
            onChange={event => setInputPage(Number(event.target.value))}
            value={inputPage === 0 ? '0' : inputPage ? inputPage.toString().replace(/^0+/, '') : ''}
            tabIndex={1}
          />
        </form>
      </div>
    </div>
  );
};

export default Pagination;
